/**
 * `/agents` — the sixteen specialists behind one assistant.
 *
 * The port of the demo's agent roster (`AG` at demo line 2481, rendered at
 * demo line 2514, with the two explainer cards in the `v-agents` markup at
 * demo line 1453).
 *
 * One card per agent: the module swatch it belongs to, an autonomy pill, what
 * it reads, what it produces, the sentence describing its last run, and a link
 * to the operating-model module it runs in. `?agent=<name>` selects a card and
 * scrolls it into view — the demo's own `renderAgents(sel)` behaviour, and
 * what `routeForView('agents', name)` produces.
 *
 * ## Data
 *
 * Only the "Last run" row is live, and it needs four lazy domains plus about
 * thirty per-machine Maximo records. Until they settle the cards render in
 * full with that one row as a skeleton — the description, the inputs, the
 * outputs and the level are all static.
 *
 * @see src/versions/v1/modules/integration/AgentsScene.transform.ts
 * @see src/versions/v1/shared/seed/agents.ts
 * @see docs/logic/integration/agents.md
 */

import { useEffect, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { HelpAnchor, SurfacePageBody, SurfacePageHeader, SurfacePageLayout } from '@/design-system';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '../../chrome-oem/AskStrip';
import { unpackTable } from '../../shared/columnar';
import { useOemData, type WorkOrder } from '../../shared/useOemData';
import {
  agentCards,
  agentCountChip,
  agentLastRunContext,
  MATURITY,
  maximoMachinesNeeded,
  RUNTIME_NOTES,
  type AgentsSceneCtx,
} from './AgentsScene.transform';
import { useMaximoRecords } from './useMaximoRecords';

/** Demo line 1453 — the view standfirst, verbatim. */
const STANDFIRST =
  'Sixteen specialists behind one assistant. The assistant answers by calling the workspace’s ' +
  'own query functions over the embedded data and shows which queries it ran.';

export function AgentsScene() {
  const [search] = useSearchParams();
  const selected = search.get('agent');

  const workOrders = useOemData('work-orders');
  const parts = useOemData('parts');
  const service = useOemData('service');
  const machineIds = useMemo(() => maximoMachinesNeeded(), []);
  const maximo = useMaximoRecords(machineIds);

  const ctx = useMemo<AgentsSceneCtx | null>(() => {
    if (!workOrders.data || !parts.data || !service.data || maximo.loading) return null;
    return {
      workOrders: unpackTable<WorkOrder>(workOrders.data.wos),
      orders: parts.data.orders,
      parts: parts.data.parts,
      inventory: parts.data.inventory,
      cases: service.data.cases,
      techJobs: service.data.tapp_jobs,
      contracts: service.data.mx_contracts,
      maximo: maximo.records,
    };
  }, [maximo.loading, maximo.records, parts.data, service.data, workOrders.data]);

  const lastRunCtx = useMemo(() => (ctx ? agentLastRunContext(ctx) : null), [ctx]);
  const cards = useMemo(() => agentCards(lastRunCtx), [lastRunCtx]);

  // Demo line 2515 — `renderAgents(sel)` scrolls the selected card into view.
  const listRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!selected || !listRef.current) return;
    const target = listRef.current.querySelector(
      `#ag-${CSS.escape(selected.replace(/\W/g, ''))}`,
    );
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [selected]);

  return (
    <SurfacePageLayout width="full">
      <Commentable name="AgentsScene">
        <SurfacePageBody className="@container max-w-[1320px] space-y-8" as="div">
          <SurfacePageHeader
            kicker="Integration"
            title="Agents"
            description={STANDFIRST}
            actions={
              <div className="flex items-center gap-2">
                <span className="mono rounded-full bg-zinc-100 px-2.5 py-1 text-[11px] text-muted dark:bg-zinc-800">
                  {agentCountChip()}
                </span>
                <HelpAnchor doc="integration/agents" />
              </div>
            }
          />

          <AskStrip view="agents" />

          <Commentable name="AgentRuntimeCard">
            <section
              className="rounded-xl border p-4"
              style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
            >
              <h2
                className="text-[10px] font-semibold uppercase tracking-[0.1em]"
                style={{ color: 'var(--ink-tertiary)' }}
              >
                How the assistant runs
              </h2>
              <div className="mt-3 grid grid-cols-1 gap-4 @3xl:grid-cols-3">
                {RUNTIME_NOTES.map((note) => (
                  <div key={note.heading}>
                    <p
                      className="text-[12.5px] font-semibold"
                      style={{ color: 'var(--ink-primary)' }}
                    >
                      {note.heading}
                    </p>
                    <p
                      className="mt-1 text-[11.5px] leading-[1.6]"
                      style={{ color: 'var(--ink-secondary)' }}
                    >
                      {note.body}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          </Commentable>

          <Commentable name="AgentRoster">
            <div
              ref={listRef}
              className="grid grid-cols-1 gap-3 @2xl:grid-cols-2 @5xl:grid-cols-3"
            >
              {cards.map((card) => {
                const isSelected = selected === card.agent.name;
                return (
                  <article
                    key={card.agent.name}
                    id={card.domId}
                    className={cn(
                      'flex min-w-0 flex-col gap-2 rounded-xl border p-4',
                      isSelected ? 'ring-2' : '',
                    )}
                    style={{
                      borderColor: 'var(--hairline)',
                      background: 'var(--surface-2)',
                      ...(isSelected ? { boxShadow: '0 0 0 2px var(--accent)' } : {}),
                    }}
                  >
                    <div className="flex items-start gap-2">
                      <span
                        aria-hidden="true"
                        className="mt-1 inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                        style={{ background: card.colour }}
                      />
                      <h3
                        className="min-w-0 flex-1 text-[13.5px] font-semibold leading-tight tracking-tight"
                        style={{ color: 'var(--ink-primary)' }}
                      >
                        {card.agent.name}
                      </h3>
                      <span
                        className={`band-pill shrink-0 ${card.agent.level === 'L2' ? 'band-good' : 'band-fair'}`}
                      >
                        {card.levelLabel}
                      </span>
                    </div>

                    <p
                      className="text-[11.5px] leading-[1.55]"
                      style={{ color: 'var(--ink-secondary)' }}
                    >
                      {card.agent.description}
                    </p>

                    <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-1 text-[11px] leading-snug">
                      <dt
                        className="font-semibold uppercase tracking-[0.06em]"
                        style={{ color: 'var(--ink-tertiary)' }}
                      >
                        Reads
                      </dt>
                      <dd style={{ color: 'var(--ink-secondary)' }}>{card.agent.reads}</dd>
                      <dt
                        className="font-semibold uppercase tracking-[0.06em]"
                        style={{ color: 'var(--ink-tertiary)' }}
                      >
                        Produces
                      </dt>
                      <dd style={{ color: 'var(--ink-secondary)' }}>{card.agent.produces}</dd>
                    </dl>

                    <div
                      className="mt-auto rounded-lg px-2.5 py-2"
                      style={{ background: 'var(--surface-3)' }}
                    >
                      <span
                        className="block text-[10px] font-semibold uppercase tracking-[0.08em]"
                        style={{ color: 'var(--ink-tertiary)' }}
                      >
                        Last run
                      </span>
                      {card.lastRun ? (
                        <span
                          className="mt-0.5 block text-[11.5px] leading-snug"
                          style={{ color: 'var(--ink-primary)' }}
                        >
                          {card.lastRun}
                        </span>
                      ) : (
                        <span
                          aria-hidden="true"
                          className="mt-1.5 block h-[11px] w-4/5 animate-pulse rounded-full"
                          style={{ background: 'var(--hairline)' }}
                        />
                      )}
                    </div>

                    {card.moduleTo && card.moduleLabel ? (
                      <Link
                        to={card.moduleTo}
                        className="inline-flex items-center gap-1 self-start text-[11.5px] font-medium underline decoration-dotted underline-offset-[3px] transition-colors hover:decoration-solid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                        style={{ color: 'var(--accent)' }}
                      >
                        {card.moduleLabel}
                        <ChevronRight className="h-3 w-3" aria-hidden="true" />
                      </Link>
                    ) : null}
                  </article>
                );
              })}
            </div>
          </Commentable>

          <Commentable name="AgentMaturityCard">
            <section
              className="rounded-xl border p-4"
              style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
            >
              <h2
                className="text-[10px] font-semibold uppercase tracking-[0.1em]"
                style={{ color: 'var(--ink-tertiary)' }}
              >
                Maturity model · where each mine sits
              </h2>
              <ul className="mt-3 grid grid-cols-1 gap-2 @3xl:grid-cols-2">
                {MATURITY.map((rung) => (
                  <li
                    key={rung.level}
                    className="rounded-lg border px-3 py-2"
                    style={{
                      borderColor: rung.active ? 'var(--accent)' : 'var(--hairline)',
                      background: rung.active ? 'var(--accent-soft)' : 'transparent',
                    }}
                  >
                    <b
                      className="block text-[12px]"
                      style={{ color: rung.active ? 'var(--accent)' : 'var(--ink-secondary)' }}
                    >
                      {rung.level}
                    </b>
                    <span
                      className="mt-0.5 block text-[11px] leading-snug"
                      style={{ color: 'var(--ink-tertiary)' }}
                    >
                      {rung.note}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          </Commentable>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
